import React, { useState, useEffect } from 'react';
import { withRouter } from 'react-router-dom';

import PropTypes from 'prop-types';
import {
  MDBPagination,
  MDBPageItem,
  MDBPageNav,
  MDBCol,
  MDBRow,
} from 'mdbreact';

import ContentLoader from 'components/Informations/Loaders/ContentLoader';
import FilledButton from 'components/Buttons/FilledButton';
import Page from 'components/PageStructure';

import FilterField from 'components/FormGroup/FilterField';

import Toast from 'components/Informations/Toast';

import endpoints from 'services/endpoints';
import useFetch from 'hooks/useFetch';

import Table from './InternalComponents/EmployeesTable';

import * as S from './styles';

const { getEmployees } = endpoints;

function Employees({ history, user }) {
  const { administrador } = user;
  const isAdmin = administrador === 1;

  const [isLoading, setIsLoading] = useState(false);

  const [toastVisibility, setToastVisiblity] = useState(true);
  const [toastType, setToastType] = useState('');
  const [toastMessage, setToastMessage] = useState('');
  const [toastTime, setToastTime] = useState(2000);

  const [searchEmployees, setSearchEmployees] = useState([]);
  const [employeeSearched, setEmployeeSearched] = useState('');

  const [pageIndex, setPageIndex] = useState(1);
  const [employees, setEmployees] = useState([]);
  const [quantityPages, setQuantityPages] = useState(0);

  const [allEmployees] = useFetch(`${getEmployees}`);
  const [paginatedEmployees] = useFetch(`${getEmployees}/${pageIndex}`);

  useEffect(() => {
    const toast =
      localStorage.getItem('successToast') !== null || undefined
        ? JSON.parse(localStorage.getItem('successToast'))
        : {};

    if (Object.keys(toast).length) {
      const {
        toastType: type,
        toastMessage: message,
        toastCloseTime: closeTime,
      } = toast;

      setToastVisiblity(true);
      setToastTime(closeTime);
      setToastMessage(message);
      setToastType(type);

      setTimeout(() => {
        setToastVisiblity(false);
        setToastMessage('');
        setToastType('');
      }, closeTime);
    }

    return () => {
      setToastTime(2000);
      setToastMessage('');
      setToastType('');
      localStorage.removeItem('successToast');
    };
  }, []);

  useEffect(() => {
    (async () => {
      setIsLoading(true);
      const { data, funcionariosTotal } = await paginatedEmployees;
      const totalEmployees = await allEmployees;
      const paginationArr = Array.from(
        { length: funcionariosTotal / 10 + (funcionariosTotal % 10 ? 1 : 0) },
        y => y + 1,
      );

      const isLoaded = !!data && !!totalEmployees;

      setEmployees(data);
      setQuantityPages(paginationArr);
      setSearchEmployees(totalEmployees);
      setIsLoading(!isLoaded);
    })();
  }, [paginatedEmployees, allEmployees]);

  const handleSearch = () => {
    const filteredEmployees = searchEmployees.filter(employee => {
      const { nome } = employee;
      return nome
        ? nome.toLowerCase().indexOf(employeeSearched.toLowerCase()) >= 0
        : false;
    });

    return <Table employees={filteredEmployees} />;
  };

  return (
    <>
      {isLoading && (
        <S.Loader>
          <ContentLoader />
        </S.Loader>
      )}

      {toastVisibility && (
        <Toast
          type={toastType}
          message={toastMessage}
          autoClose={toastTime}
          hideProgress
        />
      )}

      <Page
        title="Gerencie todos os funcionários cadastrados"
        subtitle="Cadastre, visualize e edite os funcionários da empresa."
      >
        <S.Actions>
          <div>
            <FilterField
              handleChange={e => setEmployeeSearched(e.target.value)}
              value={employeeSearched}
              name="filterEmployees"
              labelText="Deseja pesquisar um funcionário em específico?"
              width={400}
              placeholderText="Insira o nome do funcionário"
              noTransparency={false}
              disabled={!searchEmployees}
            />
          </div>
          {isAdmin && (
            <FilledButton
              text="+ Adicionar semântico"
              buttonClass="success"
              handleClick={() => history.push('/auth/employees/add-employee')}
            />
          )}
        </S.Actions>

        <MDBRow>
          <MDBCol lg="12">
            {employeeSearched.length > 0 ? (
              handleSearch()
            ) : (
              <Table employees={employees} />
            )}
          </MDBCol>
        </MDBRow>

        {!employeeSearched && (
          <MDBRow>
            <MDBCol lg="12">
              <MDBPagination
                size="lg"
                className="d-inline-flex justify-content-center mb-3 mt-5 center w-100"
                color="blue"
              >
                <MDBPageItem disabled={pageIndex === 1}>
                  <MDBPageNav
                    aria-label="Previous"
                    className="page-link"
                    onClick={() => setPageIndex(1)}
                  >
                    <span>Primeira</span>
                  </MDBPageNav>
                </MDBPageItem>

                <MDBPageItem disabled={pageIndex === 1}>
                  <MDBPageNav
                    onClick={() => setPageIndex(prev => prev - 1)}
                    className="page-link"
                  >
                    <span aria-hidden="true">&laquo;</span>
                  </MDBPageNav>
                </MDBPageItem>

                <MDBPageItem>
                  <MDBPageNav>
                    {pageIndex}
                    <span className="sr-only">(current)</span>
                  </MDBPageNav>
                </MDBPageItem>

                <MDBPageItem disabled={pageIndex >= quantityPages.length}>
                  <MDBPageNav
                    onClick={() => setPageIndex(prev => prev + 1)}
                    className="page-link"
                  >
                    <span aria-hidden="true">&raquo;</span>
                  </MDBPageNav>
                </MDBPageItem>

                <MDBPageItem disabled={pageIndex >= quantityPages.length}>
                  <MDBPageNav
                    aria-label="Next"
                    onClick={() => setPageIndex(quantityPages.length)}
                    className="page-link"
                  >
                    <span>Última</span>
                  </MDBPageNav>
                </MDBPageItem>
              </MDBPagination>
            </MDBCol>
          </MDBRow>
        )}
      </Page>
    </>
  );
}

export default withRouter(Employees);

Employees.defaultProps = {
  user: {},
};

Employees.propTypes = {
  history: PropTypes.shape({
    push: PropTypes.func,
  }).isRequired,
  user: PropTypes.shape(
    PropTypes.oneOfType([PropTypes.string, PropTypes.number, PropTypes.bool]),
  ),
};
